import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/circle.default.js';
import * as wireframeSkin from './skins/circle.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const VALID_SIZES    = ['xs', 'sm', 'md', 'lg', 'xl'];
const VALID_VARIANTS = ['outline', 'solid', 'ghost'];
const VALID_INTENTS  = ['neutral', 'primary', 'success', 'warning', 'danger', 'info'];
const DEFAULT_SIZE    = 'md';
const DEFAULT_VARIANT = 'outline';
const DEFAULT_INTENT  = 'neutral';

class RunCircle extends RunElement {
  #size    = DEFAULT_SIZE;
  #variant = DEFAULT_VARIANT;
  #intent  = DEFAULT_INTENT;
  #label   = '';
  #rendering = false;

  static get observedAttributes() {
    return ['size', 'variant', 'intent', 'label'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'img');
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'size':    this.size    = newValue; break;
      case 'variant': this.variant = newValue; break;
      case 'intent':  this.intent  = newValue; break;
      case 'label':   this.label   = newValue; break;
    }
  }

  get size() { return this.#size; }
  set size(value) {
    const trimmed = String(value == null ? '' : value).trim();
    const coerced = VALID_SIZES.includes(trimmed) ? trimmed : DEFAULT_SIZE;
    if (this.#size === coerced) return;
    this.#size = coerced;
    this.setAttribute('size', coerced);
    this.requestUpdate();
  }

  get variant() { return this.#variant; }
  set variant(value) {
    const trimmed = String(value == null ? '' : value).trim();
    const coerced = VALID_VARIANTS.includes(trimmed) ? trimmed : DEFAULT_VARIANT;
    if (this.#variant === coerced) return;
    this.#variant = coerced;
    this.setAttribute('variant', coerced);
    this.requestUpdate();
  }

  get intent() { return this.#intent; }
  set intent(value) {
    const trimmed = String(value == null ? '' : value).trim();
    const coerced = VALID_INTENTS.includes(trimmed) ? trimmed : DEFAULT_INTENT;
    if (this.#intent === coerced) return;
    this.#intent = coerced;
    this.setAttribute('intent', coerced);
    this.requestUpdate();
  }

  get label() { return this.#label; }
  set label(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#label === coerced) return;
    this.#label = coerced;
    if (coerced) {
      this.setAttribute('label', coerced);
    } else {
      this.removeAttribute('label');
    }
    this.#updateAria();
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    if (this.#label) {
      this.setAttribute('aria-label', this.#label);
      this.removeAttribute('aria-hidden');
    } else {
      this.setAttribute('aria-hidden', 'true');
      this.removeAttribute('aria-label');
    }
  }
}

if (!customElements.get('run-circle')) {
  customElements.define('run-circle', RunCircle);
}

export { RunCircle };
export default RunCircle;